import {
  Placement,
  PlacementArea,
  PlacementManager,
  PlacementReference,
  Text,
  Divider,
} from '@bitrise/bitkit';
import { VizContext } from '@bitrise/bitviz';
import { SessionSpan } from '@trace/types';
import React, { FunctionComponent, ReactNode, useContext, useState } from 'react';
import { ReferenceChildrenProps } from 'react-popper';
import { formatterDuration } from '../../../utils/getFormattedAttributeValue';
import { formatterMs } from '../../../utils/metric/getFormattedValue';

interface SessionTraceVisualisationTooltipChildrenProps extends ReferenceChildrenProps {
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
}

interface Props {
  children: (props: SessionTraceVisualisationTooltipChildrenProps) => ReactNode;
  span: SessionSpan;
}

const SessionTraceVisualisationTooltip: FunctionComponent<Props> = (props: Props) => {
  const { children, span } = props;
  const [visible, setVisible] = useState(false);
  const { xDomain } = useContext(VizContext);

  const handlePointerEnter = () => setVisible(true);
  const handlePointerLeave = () => setVisible(false);

  return (
    <PlacementManager>
      <Placement
        backgroundColor="black"
        borderRadius="x1"
        placement="top"
        textColor="gray-1"
        visible={visible}
      >
        {() => (
          <PlacementArea paddingHorizontal="x3" paddingVertical="x2" withArrow>
            <Text margin="x2">
              <Text>{span.name}</Text>
              <Divider color="gray-8" margin="x1" width="0.0625rem" />
            </Text>

            <Text margin="x2">
              <Text>
                Starts at
                {formatterMs(span.start - xDomain[0])}
              </Text>
            </Text>

            <Text margin="x2">
              <Text config="8" textColor="gray-5">
                Duration
              </Text>
              <Text>{formatterDuration(span.durationMs)}</Text>
            </Text>

            {Object.entries(span.attributes).map(([key, value]) => (
              <Text key={key} margin="x2">
                <Text config="8" textColor="gray-5">
                  {key}:
                </Text>
                <Text config="8">{value}</Text>
              </Text>
            ))}
          </PlacementArea>
        )}
      </Placement>

      <PlacementReference>
        {(placementReferenceProps) =>
          children({
            ...placementReferenceProps,
            onPointerEnter: handlePointerEnter,
            onPointerLeave: handlePointerLeave,
          })
        }
      </PlacementReference>
    </PlacementManager>
  );
};

export default SessionTraceVisualisationTooltip;
